import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';

import DefaultApiLimit from '../../../../config/defaultLimit';
import {
  ConsumerGuestChargingInterFace,
  RequestConsumerGuestChargingInterFace,
} from '../../../../interface/end-consumer-guest-charging-interface';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { setSelectedDevice } from '../../../../redux/slices/DeviceManagementSlice';
import {
  fetchConsumerGuestCharging,
  fetchConsumerGuestChargingFilter,
  setSortData,
  updateConsumerGuestChargingListFilter,
} from '../../../../redux/slices/EnsConsumerGuestCharging';
import { setNotificationMessage } from '../../../../redux/slices/NotificationManagementSlice';
import CustomTable from '../../../../shared-components/custom-table/CustomTable';
import DeviceSidePanel from '../../../../shared-components/table-side-panel/DeviceSidePanel';
import LocationSidePanel from '../../../../shared-components/table-side-panel/LocationSidePanel';
import SidePaneModel from '../../../../shared-components/table-side-panel/SidePaneModel';
import { GetParametersFromCommonFilters } from '../../../../shared-functions/CommonFilters';
import ConvertUTCtoDate from '../../../../shared-functions/ConvertUTCtoDate';
import { convertUtcTo12HourTime } from '../../../../shared-functions/DateTime';
import { exportCSV, exportPDF } from '../../../../shared-functions/ExportFile';
import endConsumerApi from '../../../../utils/api/end-consumer-v2';

interface ModelDetailsInterface {
  title?: string;
  content: JSX.Element;
  url?: string;
  urlNewTab?: string;
}

/**
 * Charge Session List
 * @returns {any} - The rendered Guest Charging List component
 */
function GuestChargingList() {
  const { t, i18n } = useTranslation('endConsumer');
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const [exportLoader, setExportLoader] = useState<boolean>(false);
  const {
    consumers,
    limit,
    page,
    totalPages,
    totalResults,
    loading,
    filterListLoading,
    filters,
    isFilterUpdated,
    sortField,
    sortBy,
  } = useAppSelector((state) => state.ConsumerGuestCharging);
  const { selectedEndConsumer, endConsumerType } = useAppSelector((state) => state.endConsumerType);
  const [modelDetails, setModelDetails] = useState<ModelDetailsInterface>({
    title: '',
    content: <div />,
    url: '',
    urlNewTab: '',
  });
  /**
   * Fetch Users list function
   * @param {any} data - The data
   */
  const fetchData = (data: RequestConsumerGuestChargingInterFace) => {
    if (!loading && selectedEndConsumer) {
      dispatch(
        fetchConsumerGuestCharging({
          userCustomId: selectedEndConsumer.userCustomId,
          ...data,
        }),
      );
    }
  };

  /**
   * Handle open station details
   * @param {any} stationId - The stationId
   * @param {any} stationName - The stationName
   */
  const handleOpenStationDetails = (stationId: string, stationName: string) => {
    navigate(`?stationId=${stationId}`);

    setIsOpen(true);
    setModelDetails({
      title: '',
      content: <LocationSidePanel fetchIdFrom="queryParams" />,
      url: `/ev-admin/station-management/${stationId}/station-dashboard?searchBy=${stationName}&source=${location.pathname}`,
    });
  };

  /**
   * Handle open end consumer details
   * @param {any} chargingPointSn - The chargingPointSn
   * @param {any} chargingPointName - The chargingPointName
   * @param {any} _id - The _id
   */
  const handleOpenDeviceDetails = (
    chargingPointSn: string,
    chargingPointName: string,
    _id: string,
  ) => {
    navigate(`?deviceId=${_id}`);
    setIsOpen(true);
    dispatch(
      setSelectedDevice({
        id: _id,
        name: chargingPointName,
        chargingPointSn,
      }),
    );
    setModelDetails({
      title: '',
      content: <DeviceSidePanel fetchIdFrom="queryParams" />,
      url: `/ev-admin/end-consumer/${endConsumerType}/profile/${selectedEndConsumer?.userCustomId}/charger/${_id}/device-details?searchBy=${chargingPointName}`,
      urlNewTab: `/ev-admin/end-consumer/${endConsumerType}/profile/${selectedEndConsumer?.userCustomId}/charger-expanded/${_id}/device-details?searchBy=${chargingPointName}`,
    });
  };

  /**
   * Charging Station
   * @param {any} facilityTag - The facility tag data
   * @returns {any} - The rendered charging station list
   */
  const renderChargingStation = (facilityTag: ConsumerGuestChargingInterFace) => (
    <div className="charging-session-station-list">
      {facilityTag?.chargingStations?.map((chargingStation) => (
        <div
          className="tag-success-label"
          style={{ cursor: 'pointer' }}
          role="presentation"
          key={chargingStation._id}
          onClick={() => {
            handleOpenStationDetails(chargingStation._id, chargingStation.name);
          }}>
          {chargingStation.name}
        </div>
      ))}
    </div>
  );

  /**
   * Charging Station
   * @param {any} facilityTag - The facility tag data
   * @returns {any} - The rendered charging devices list
   */
  const renderChargingDevices = (facilityTag: ConsumerGuestChargingInterFace) => (
    <div className="charging-session-station-list">
      {facilityTag?.chargingPoints?.map((chargingPoint) => (
        <div
          className="tag-success-label"
          style={{ cursor: 'pointer' }}
          role="presentation"
          key={chargingPoint.chargingPointSn}
          onClick={() => {
            handleOpenDeviceDetails(
              chargingPoint.chargingPointSn,
              chargingPoint.chargingPointName,
              chargingPoint._id,
            );
          }}>
          {chargingPoint.chargingPointSn}
        </div>
      ))}
    </div>
  );

  const column = [
    {
      label: t('date'),
      accessorKey: 'date' as keyof ConsumerGuestChargingInterFace,
      sortAllow: true,
      /**
       * Custom data function
       * @param {any} currentRow - current row data
       * @returns {any} - custom data
       */
      customData: (currentRow: ConsumerGuestChargingInterFace) => (
        <div>
          <p>
            {ConvertUTCtoDate(currentRow?.date)}{' '}
            {!currentRow?.isSelectiveDate && `- ${ConvertUTCtoDate(currentRow?.toDate)}`}
          </p>
          <p>{convertUtcTo12HourTime(currentRow?.date)}</p>
        </div>
      ),
    },
    {
      label: t('station'),
      accessorKey: 'chargingStations' as keyof ConsumerGuestChargingInterFace,
      sortAllow: false,
      customData: renderChargingStation,
    },
    {
      label: t('device'),
      accessorKey: 'chargingPoints' as keyof ConsumerGuestChargingInterFace,
      sortAllow: false,
      customData: renderChargingDevices,
      // /**
      //  * Rendering device name with hyper link
      //  * @param currentRow - current row data
      //  * @returns - custom data
      //  */
      // customData: (currentRow: ConsumerGuestChargingInterFace) => (
      //   <p
      //     role="presentation"
      //     className="hyper-link"
      //     onClick={() => handleOpenDeviceDetails(currentRow)}>
      //     {currentRow?.chargingPoints?.[0]?.chargingPointSn || ''}
      //   </p>
      // ),
    },
    {
      label: t('duration'),
      accessorKey: 'durationLimitInMins' as keyof ConsumerGuestChargingInterFace,
      sortAllow: true,
      /**
       * Rendering device name with hyper link
       * @param {any} currentRow - current row data
       * @returns {any} - custom data
       */
      customData: (currentRow: ConsumerGuestChargingInterFace) => (
        <p>
          {currentRow.durationLimitInMins} {t('mins')}
        </p>
      ),
    },
  ];

  /**
   * Current columns for Guest Charging export
   */
  const currentColumns = [
    { label: t('sNo'), accessorKey: '__serial__' },
    { label: t('date'), accessorKey: 'date' },
    { label: t('station'), accessorKey: 'chargingStations' },
    { label: t('device'), accessorKey: 'chargingPoints' },
    { label: t('duration'), accessorKey: 'durationLimitInMins' },
  ];

  /**
   * Filtering columns for export without hidden columns
   */
  const exportCurrentColumns = currentColumns.filter(
    (col) => col.accessorKey === '__serial__' || !hiddenColumns.includes(col.accessorKey),
  );

  /**
   * Export data function
   * @param {any} type - The type
   */
  const exportDataPdfOrCSV = (type: string) => {
    setExportLoader(true);

    const queryParameters = GetParametersFromCommonFilters(filters);
    const payload: any = { ...queryParameters };

    if (sortField) payload.sortField = sortField;
    if (sortBy) payload.sortBy = sortBy;

    if (!selectedEndConsumer?.userCustomId) return;

    endConsumerApi
      .guestChargingExport(selectedEndConsumer.userCustomId, payload)
      .then((response) => {
        /**
         * Dynamic Headers
         */
        const headers = exportCurrentColumns.map((col) => col.label);

        /**
         * Dynamic Rows
         */
        const rows = response.data.map((row: ConsumerGuestChargingInterFace, index: number) =>
          exportCurrentColumns.map((col) => {
            switch (col.accessorKey) {
              case '__serial__':
                return index + 1;

              case 'date':
                return row?.date
                  ? `${ConvertUTCtoDate(row.date)} ${convertUtcTo12HourTime(row.date)}`
                  : t('na');

              case 'chargingStations':
                return row?.chargingStations?.length
                  ? row.chargingStations.map((station) => station.name).join(', ')
                  : t('na');

              case 'chargingPoints':
                return row?.chargingPoints?.length
                  ? row.chargingPoints.map((point) => point.chargingPointSn).join(', ')
                  : t('na');

              case 'durationLimitInMins':
                return row?.durationLimitInMins || t('na');

              default:
                return (row as any)[col.accessorKey] || t('na');
            }
          }),
        );

        if (rows.length > 0) {
          if (type === 'csv') {
            exportCSV(headers, rows, 'end-consumer-guest-charging.csv');
          } else {
            exportPDF(headers, rows, 'end-consumer-guest-charging.pdf');
          }
        }

        setExportLoader(false);
      })
      .catch((error: any) => {
        setExportLoader(false);
        dispatch(
          setNotificationMessage({
            message: error?.response?.data?.message,
            status: true,
            type: 'error',
            code: error?.response?.data?.code,
          }),
        );
      });
  };

  /**
   * Table pagination function
   * @param {any} value - The pagination value
   */
  const pageNateLimitChange = (value: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({ ...queryParameters, ...value, sortField, sortBy });
  };

  /**
   * Handle the filter data clear function
   * @param {any} sortData - The sortData
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(setSortData(sortData));
    fetchData({
      ...queryParameters,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      page: DefaultApiLimit.page,
      limit,
    });
  };

  /**
   * Function to refresh data
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({ ...queryParameters, page, limit, sortField, sortBy });
  };

  /**
   * Function to close modal
   */
  const handleCloseModal = () => {
    setIsOpen(false);
    setModelDetails({ title: '', content: <div /> });
  };

  useEffect(() => {
    if (!selectedEndConsumer?.userCustomId) return;

    const currentId = selectedEndConsumer.userCustomId;
    const lastId = sessionStorage.getItem('GuestCharging');

    if (isFilterUpdated || consumers.length === 0 || lastId !== currentId) {
      if (selectedEndConsumer?.userCustomId) {
        const queryParameters = GetParametersFromCommonFilters(filters);
        fetchData({
          page: DefaultApiLimit.page,
          limit,
          ...queryParameters,
        });
      }
    }
    sessionStorage.setItem('GuestCharging', currentId);
  }, [filters, selectedEndConsumer]);

  return (
    <div className="charging-session-wrap" dir={i18n.dir()}>
      <SidePaneModel
        open={isOpen}
        title={modelDetails.title}
        content={modelDetails.content}
        closePanel={handleCloseModal}
        url={modelDetails.url}
        urlNewTab={modelDetails.urlNewTab}
        preventQueryClear
      />{' '}
      <CustomTable
        header={column}
        data={consumers}
        serialNo
        actionButton
        sizeChangeButtonRequired
        version="v2"
        height={500}
        page={page}
        limit={limit}
        loading={loading}
        totalPages={totalPages}
        totalResults={totalResults}
        sortData={{ sortBy, sortField }}
        handleSortChange={handleSortFunction}
        pageLimitChange={pageNateLimitChange}
        filters={filters}
        filtersToDispatch={updateConsumerGuestChargingListFilter}
        filterListLoading={filterListLoading}
        filterListToDispatch={fetchConsumerGuestChargingFilter}
        exportData={consumers}
        exportLoader={exportLoader}
        exportDataPdfOrCSV={exportDataPdfOrCSV}
        refreshRequired
        requiredRefreshToggle
        refershCurrentPages={handleRefresh}
        refreshFunction={handleRefresh}
        refreshInterval={10}
        leftTopContent={<> </>}
        expandTableOpen
        expandTableOpenKey="GuestChargingV2ListEndConsumer"
        onHiddenColumnsChange={setHiddenColumns}
      />
    </div>
  );
}

export default GuestChargingList;
